import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { App, Button, Dropdown, Modal, Segmented, Spin } from 'antd'
import { CopyOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'

import { getTemplatePageAction, generateTemplateAction, getPageListAction, savePageAction, deletePageAction } from '@/actions/HtTemplateActions'
import './Preview.css'

/* 空状态信函插图（信封 + 星光点缀，呼应信函主题） */
const EmptyLetterIcon = () => (
  <svg width="88" height="88" viewBox="0 0 88 88" fill="none">
    <circle cx="44" cy="44" r="40" fill="#eef7f4" />
    <rect x="20" y="30" width="48" height="34" rx="5" fill="#fff" stroke="#1ba784" strokeWidth="2.5" />
    <path d="M20 34 L44 52 L68 34" stroke="#1ba784" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M66 18l1.8 5.2 5.2 1.8-5.2 1.8-1.8 5.2-1.8-5.2-5.2-1.8 5.2-1.8z" fill="#1ba784" opacity="0.5" />
    <circle cx="21" cy="22" r="2.5" fill="#1ba784" opacity="0.35" />
  </svg>
)

/* ============================ 信函测试预览页（独立窗口） ============================ */
export default function HtTemplatePreview() {
  const { dei_sn, type } = useParams()
  const [searchParams] = useSearchParams()
  const name = searchParams.get('name') || ''
  const coli_sn = searchParams.get('coli_sn') || ''
  const lgc = searchParams.get('lgc') || ''

  const [device, setDevice] = useState('pc')
  const isMobile = device === 'mobile'

  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const [generating, setGenerating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [copying, setCopying] = useState(false)

  const [pages, setPages] = useState([])

  const [letterTitle, setLetterTitle] = useState('')
  const [title, setTitle] = useState('')
  const [summary, setSummary] = useState('')
  const [amount, setAmount] = useState('')

  const { message } = App.useApp()
  const iframeRef = useRef(null)

  const content = detail?.content || ''

  // 应用 page 行：刷新预览（content）+ 回填功能区（title/tour_title/tour_summary/amount）
  const applyPage = useCallback((page) => {
    setDetail(page)
    setLetterTitle(page?.title || '')
    setTitle(page?.tour_title || '')
    setSummary(page?.tour_summary || '')
    setAmount(String(page?.amount ?? ''))
  }, [])

  // 拉取版本列表（当前 coli_sn + type）
  const fetchPages = useCallback(async () => {
    try {
      const list = await getPageListAction({ coli_sn, type })
      setPages(list)
    } catch (e) {
      setPages([])
      message.error(e?.message || '获取版本列表失败')
    }
  }, [coli_sn, type, message])

  useEffect(() => {
    let cancelled = false
    setDetail(null)
    setDetailLoading(true)
    getTemplatePageAction({ coli_sn, dei_sn, type, lgc })
      .then((res) => { if (!cancelled) applyPage(res) })
      .catch((e) => { if (!cancelled) message.error(e?.message || '获取信函内容失败') })
      .finally(() => { if (!cancelled) setDetailLoading(false) })
    return () => { cancelled = true }
  }, [dei_sn, type, coli_sn, lgc, applyPage, message])

  useEffect(() => {
    fetchPages()
  }, [fetchPages])

  // 保存：取 iframe 实时编辑内容 + 功能区字段，写入 ht_pages
  const handleSave = async () => {
    setSaving(true)
    try {
      const editedContent = iframeRef.current?.contentDocument?.body?.innerHTML ?? content
      const res = await savePageAction({
        id: detail?.id || 0,
        coli_sn: Number(coli_sn) || 0,
        dei_sn: Number(dei_sn) || 0,
        type,
        title: letterTitle,
        content: editedContent,
        tour_title: title,
        tour_summary: summary,
        amount: Number(amount) || 0,
        link: detail?.link || '',
      })
      const list = await getPageListAction({ coli_sn, type })
      setPages(list)
      const saved = list.find((p) => p.id === (res?.id || detail?.id)) || list[0]
      if (saved) applyPage(saved)
      message.success('已保存')
    } catch (e) {
      message.error(e?.message || '保存失败')
    } finally {
      setSaving(false)
    }
  }

  // 复制当前页面：以当前页字段新增一条（id=0），标题加 -copy，并切到副本
  const handleCopy = async () => {
    if (!detail?.id) {
      message.warning('当前无页面可复制')
      return
    }
    setCopying(true)
    try {
      const newTitle = `${letterTitle || detail?.title || '未命名'}-copy`
      const res = await savePageAction({
        id: 0,
        coli_sn: Number(coli_sn) || 0,
        dei_sn: Number(dei_sn) || 0,
        type,
        title: newTitle,
        content: detail?.content || '',
        tour_title: detail?.tour_title || '',
        tour_summary: detail?.tour_summary || '',
        amount: detail?.amount || 0,
        link: detail?.link || '',
      })
      const list = await getPageListAction({ coli_sn, type })
      setPages(list)
      const copy = list.find((p) => p.id === res?.id)
      if (copy) applyPage(copy)
      message.success('已复制')
    } catch (e) {
      message.error(e?.message || '复制失败')
    } finally {
      setCopying(false)
    }
  }

  // 生成信函：已有页面先备份当前编辑为 AUTOSAVE-N 历史（最多 10 份），再调 generate 生成新版本
  const handleGenerateLetter = async () => {
    const wasExisting = !!detail?.id
    setGenerating(true)
    try {
      let autosaveNo = 0
      if (wasExisting) {
        const editedContent = iframeRef.current?.contentDocument?.body?.innerHTML ?? detail?.content ?? ''
        const autosaves = pages
          .filter((p) => /^AUTOSAVE-\d+$/.test(p.title || ''))
          .map((p) => ({ id: p.id, no: Number(p.title.replace('AUTOSAVE-', '')) }))
          .sort((a, b) => a.no - b.no)
        if (autosaves.length >= 10) {
          await deletePageAction(autosaves[0].id)
        }
        autosaveNo = (autosaves.length ? autosaves[autosaves.length - 1].no : 0) + 1
        await savePageAction({
          id: 0,
          coli_sn: Number(coli_sn) || 0,
          dei_sn: Number(dei_sn) || 0,
          type,
          title: `AUTOSAVE-${autosaveNo}`,
          content: editedContent,
          tour_title: detail?.tour_title || '',
          tour_summary: detail?.tour_summary || '',
          amount: detail?.amount || 0,
          link: detail?.link || '',
        })
      }
      const res = await generateTemplateAction({ coli_sn, dei_sn, type, lgc })
      if (!res?.html) {
        message.warning('生成结果为空')
        return
      }
      const page = await getTemplatePageAction({ coli_sn, dei_sn, type, lgc })
      applyPage(page)
      await fetchPages()
      message.success(wasExisting ? `已重新生成，历史已存为 AUTOSAVE-${autosaveNo}` : `已生成「${type}」`)
    } catch (e) {
      message.error(e?.message || '生成信函失败')
    } finally {
      setGenerating(false)
    }
  }

  // 删除版本：确认后删库 → 刷新列表；删的是当前页则切到列表第一个版本
  const handleDeletePage = (p) => {
    Modal.confirm({
      title: '删除版本',
      content: `确认删除版本「${p.title || '未命名'}」？此操作不可恢复。`,
      okType: 'danger',
      okText: '删除',
      cancelText: '取消',
      onOk: async () => {
        try {
          await deletePageAction(p.id)
          const list = await getPageListAction({ coli_sn, type })
          setPages(list)
          if (detail?.id === p.id) applyPage(list[0] || null)
          message.success('版本已删除')
        } catch (e) {
          message.error(e?.message || '删除失败')
        }
      },
    })
  }

  const previewHtml = useMemo(() => {
    if (!content) return ''
    return '<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{margin:0;padding:24px;font-family:"PingFang SC","Microsoft YaHei","Noto Sans SC",sans-serif;color:#1f1f1f;line-height:1.7;font-size:14px;background:#fff;outline:none;caret-color:#1ba784}img{max-width:100%}</style></head><body contenteditable="true">' + content + '</body></html>'
  }, [content])

  const Sparkle = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ display: 'block' }}>
      <path d="M12 2l2.2 6.8L21 11l-6.8 2.2L12 20l-2.2-6.8L3 11l6.8-2.2z"/>
    </svg>
  )

  return (
    <div className='pm-page'>
      <div className='pm-header'>
        <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className={`pm-type-chip ${type === '报价信' ? 'quote' : 'confirm'}`}>{type}</span>
          <span style={{ fontSize: 16, fontWeight: 600, color: '#1f1f1f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{letterTitle || name}</span>
        </div>
        <Segmented
          options={[{ label: 'PC 端预览', value: 'pc' }, { label: '移动端预览', value: 'mobile' }]}
          value={device}
          onChange={setDevice}
        />
        <div style={{ justifySelf: 'end', display: 'flex', gap: 8 }}>
          <Button type='primary' icon={<ReloadOutlined />} loading={generating} onClick={handleGenerateLetter}>
            {content ? '重新生成' : '新建'}
          </Button>
          <Dropdown.Button
            type='primary'
            loading={saving || copying}
            onClick={handleSave}
            menu={{
              items: [{ key: 'saveAs', icon: <CopyOutlined />, label: '另存为', disabled: !detail?.id }],
              onClick: ({ key }) => { if (key === 'saveAs') handleCopy() },
            }}
          >
            保存
          </Dropdown.Button>
        </div>
      </div>

      <div className='pm-body'>
        <div className='pm-canvas'>
          {detailLoading && !content ? (
            <Spin size='large' />
          ) : !content ? (
            <div className='pm-empty'>
              <div className='pm-empty-icon'><EmptyLetterIcon /></div>
              <div className='pm-empty-text'>
                <div className='pm-empty-title'>暂无信函内容</div>
                <div className='pm-empty-sub'>点击右上角「新建」生成信函</div>
              </div>
            </div>
          ) : (
            <iframe
              ref={iframeRef}
              className='pm-iframe'
              data-device={isMobile ? 'mobile' : 'pc'}
              srcDoc={previewHtml}
              sandbox='allow-scripts allow-same-origin'
              title='信函预览'
            />
          )}
        </div>

        <aside className={`pm-panel${!content ? ' pm-panel-disabled' : ''}`}>
          <div className='pm-panel-header'>
            <div className='pm-panel-title'>功能区</div>
            <div className='pm-panel-sub'>编辑与发布你的网页</div>
          </div>

          <div className='pm-field'>
            <div className='pm-field-label strong'>信函名称</div>
            <div className='pm-input-row'>
              <input className='pm-input' value={letterTitle} onChange={(e) => setLetterTitle(e.target.value)} placeholder='信函名称' />
            </div>
          </div>

          <div className='pm-divider' />

          <div className='pm-field'>
            <div className='pm-summary-head'>
              <div className='pm-field-label strong'>行程标题</div>
              <button className='pm-ai-btn' tabIndex={-1}><Sparkle /><span>AI 生成</span></button>
            </div>
            <div className='pm-input-row'>
              <input className='pm-input' value={title} onChange={(e) => setTitle(e.target.value)} placeholder='xx的行程' />
            </div>
          </div>

          <div className='pm-divider' />

          <div className='pm-field'>
            <div className='pm-summary-head'>
              <div className='pm-field-label strong'>行程概览</div>
              <button className='pm-ai-btn' tabIndex={-1}><Sparkle /><span>AI 生成</span></button>
            </div>
            <div className='pm-summary-box'>
              <textarea className='pm-summary-input' value={summary} onChange={(e) => setSummary(e.target.value)} placeholder='由 AI 自动总结，或手动输入内容…' />
            </div>
          </div>

          <div className='pm-field'>
            <div className='pm-field-label strong'>金额</div>
            <div className='pm-amount-box'>
              <div className='pm-amount-cur'>¥</div>
              <input className='pm-amount-input' type='number' step='0.01' min='0' value={amount} onChange={(e) => setAmount(e.target.value)} placeholder='0.00' />
            </div>
          </div>

          <div className='pm-action-row'>
            <button className='pm-btn pm-btn-outline' tabIndex={-1}>获取链接</button>
            <button className='pm-btn pm-btn-primary' tabIndex={-1}>发送邮件</button>
          </div>

          <div className='pm-divider' />

          <div className='pm-version-title'>版本管理</div>
          <div className='pm-version-list'>
            {pages.length === 0 ? (
              <div className='pm-version-empty'>暂无版本</div>
            ) : (
              pages.map((p) => (
                <div
                  key={p.id}
                  className={`pm-version-row${p.id === detail?.id ? ' active' : ''}`}
                >
                  <div className='pm-version-left'>
                    <div className='pm-version-name'>{p.title || '未命名'}</div>
                    <div className='pm-version-time'>{p.update_time ? dayjs(p.update_time).format('MM-DD HH:mm') : '-'}</div>
                  </div>
                  <div className='pm-version-actions'>
                    <button className='pm-preview-btn' onClick={() => applyPage(p)}>
                      打开
                    </button>
                    <button className='pm-version-del' title='删除版本' onClick={() => handleDeletePage(p)}>
                      <DeleteOutlined />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </aside>
      </div>
    </div>
  )
}
